import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchResults } from '@/features/ticket-admin/SearchResults';
import { useCreateTrackedItem, extractErrorMessage } from '@/queries/ticketMutations';
import { useCompanySearch, useUserSearch } from '@/queries/people';
import { useTrackerCategories } from '@/queries/tracker';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { priorityLabels, trackerStatusLabels, waitingOnLabel, waitingOnOrder } from '@/lib/status';
import type { CompanySearchResult, TicketPriority, UserSearchResult } from '@/types/ticket';
import type { TrackerWaitingOn } from '@/types/tracker';

const selectClass =
  'h-[var(--etn-control-height-input)] w-full rounded-etn-menu border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell';

// A picked value, shown as a chip with a clear button in place of its search box.
function Picked({ label, children, onClear }: { label: string; children: ReactNode; onClear: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-etn-menu border border-etn-border-input bg-etn-surface px-3.5 py-2 text-etn-control">
      <div className="min-w-0 truncate">{children}</div>
      <Button type="button" variant="ghost" size="icon-xs" aria-label={`Clear ${label}`} onClick={onClear}>
        <X />
      </Button>
    </div>
  );
}

function personName(user: UserSearchResult): string {
  return user.name || user.email || user._id;
}

interface Draft {
  subject: string;
  description: string;
  category: string;
  priority: TicketPriority;
  status: 'new' | 'accepted';
  nextAction: string;
  waitingOn: TrackerWaitingOn | '';
  expectedDate: string;
  notify: boolean;
}

const EMPTY_DRAFT: Draft = {
  subject: '',
  description: '',
  category: '',
  priority: 'normal',
  status: 'new',
  nextAction: '',
  waitingOn: '',
  expectedDate: '',
  notify: true
};

// Staff-only: log an item on a client's tracker on their behalf, usually
// straight out of a meeting. The ticket belongs to the picked requester.
export function CreateTrackedItemDialog({ trigger }: { trigger: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>New tracked item</DialogTitle>
          <DialogDescription>
            Adds an item to a client&apos;s tracker on their behalf. The company's Company Admins can see it, and the requester owns it.
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so closing discards the draft and nothing is fetched before it opens. */}
        <CreateTrackedItemForm onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

function CreateTrackedItemForm({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanySearchResult | null>(null);
  const [requester, setRequester] = useState<UserSearchResult | null>(null);
  const [followers, setFollowers] = useState<UserSearchResult[]>([]);
  const [companyQuery, setCompanyQuery] = useState('');
  const [requesterQuery, setRequesterQuery] = useState('');
  const [followerQuery, setFollowerQuery] = useState('');
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateTrackedItem();

  const debouncedCompany = useDebouncedValue(companyQuery);
  const debouncedRequester = useDebouncedValue(requesterQuery);
  const debouncedFollower = useDebouncedValue(followerQuery);
  const companies = useCompanySearch(company ? '' : debouncedCompany);
  // Both people searches are narrowed to the picked company's members.
  const requesters = useUserSearch(company && !requester ? debouncedRequester : '', company?._id);
  const followerResults = useUserSearch(company ? debouncedFollower : '', company?._id);
  const categoriesQuery = useTrackerCategories(company?._id);
  const categories = categoriesQuery.data ?? [];

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft(prev => ({ ...prev, [key]: value }));

  const clearCompany = () => {
    setCompany(null);
    setRequester(null);
    setFollowers([]);
    set('category', '');
  };

  const ready = Boolean(company && requester && draft.subject.trim() && draft.description.trim() && draft.category);

  const submit = async () => {
    if (!company || !requester || !ready) return;
    setError(null);
    try {
      const ticket = await create.mutateAsync({
        companyId: company._id,
        requesterId: requester._id,
        subject: draft.subject.trim(),
        description: draft.description.trim(),
        category: draft.category,
        priority: draft.priority,
        status: draft.status,
        nextAction: draft.nextAction.trim() || undefined,
        waitingOn: draft.waitingOn || null,
        // A bare date parses as UTC midnight, matching the property band's expected date.
        expectedDate: draft.expectedDate ? new Date(draft.expectedDate).toISOString() : null,
        followerIds: followers.map(f => f._id),
        notify: draft.notify
      });
      toast.success('Added to the tracker');
      onDone();
      navigate({ to: '/tracker/$id', params: { id: ticket._id } });
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not add the item'));
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="tracked-item-company">Company</Label>
          {company ? (
            <Picked label="company" onClear={clearCompany}>
              <span className="font-semibold text-etn-text-cell">{company.name || company._id}</span>
            </Picked>
          ) : (
            <>
              <Input
                id="tracked-item-company"
                autoComplete="off"
                placeholder="Search companies"
                value={companyQuery}
                onChange={e => setCompanyQuery(e.target.value)}
              />
              <SearchResults
                label="Matching companies"
                term={companyQuery}
                items={companies.data}
                isFetching={companies.isFetching || companyQuery !== debouncedCompany}
                onPick={c => {
                  setCompany(c);
                  setCompanyQuery('');
                }}
                renderItem={c => <span className="text-etn-text-cell">{c.name || c._id}</span>}
              />
            </>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="tracked-item-requester">Requester</Label>
          {requester ? (
            <Picked label="requester" onClear={() => setRequester(null)}>
              <span className="font-semibold text-etn-text-cell">{personName(requester)}</span>
              {requester.email && <span className="ml-2 text-etn-micro text-etn-text-soft">{requester.email}</span>}
            </Picked>
          ) : (
            <>
              <Input
                id="tracked-item-requester"
                autoComplete="off"
                disabled={!company}
                placeholder={company ? 'Search people at this company' : 'Pick a company first'}
                value={requesterQuery}
                onChange={e => setRequesterQuery(e.target.value)}
              />
              <SearchResults
                label="Matching requesters"
                term={requesterQuery}
                items={requesters.data?.filter(u => !u.isAdmin)}
                isFetching={requesters.isFetching || requesterQuery !== debouncedRequester}
                onPick={u => {
                  setRequester(u);
                  setRequesterQuery('');
                  setFollowers(prev => prev.filter(f => f._id !== u._id));
                }}
                renderItem={u => (
                  <>
                    <div className="truncate font-semibold text-etn-text-cell">{personName(u)}</div>
                    {u.email && <div className="truncate text-etn-micro text-etn-text-soft">{u.email}</div>}
                  </>
                )}
              />
            </>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="tracked-item-subject">Subject</Label>
          <Input id="tracked-item-subject" maxLength={200} value={draft.subject} onChange={e => set('subject', e.target.value)} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tracked-item-description">Description</Label>
          <Textarea
            id="tracked-item-description"
            rows={4}
            placeholder="Posted as your first message, visible to the client"
            value={draft.description}
            onChange={e => set('description', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="tracked-item-category">Category</Label>
            <select
              id="tracked-item-category"
              className={selectClass}
              disabled={!company || categoriesQuery.isLoading || categoriesQuery.isError}
              value={draft.category}
              onChange={e => set('category', e.target.value)}
            >
              <option value="">{company ? 'Choose' : 'Pick a company'}</option>
              {categories.map(c => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            {categoriesQuery.isError && (
              <p className="text-etn-micro text-etn-danger-text">
                Couldn&apos;t load categories.{' '}
                <button type="button" className="underline" onClick={() => categoriesQuery.refetch()}>
                  Retry
                </button>
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="tracked-item-priority">Priority</Label>
            <select
              id="tracked-item-priority"
              className={selectClass}
              value={draft.priority}
              onChange={e => set('priority', e.target.value as TicketPriority)}
            >
              {(['high', 'normal', 'low'] as TicketPriority[]).map(p => (
                <option key={p} value={p}>
                  {priorityLabels[p]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tracked-item-status">Status</Label>
            <select
              id="tracked-item-status"
              className={selectClass}
              value={draft.status}
              onChange={e => set('status', e.target.value as Draft['status'])}
            >
              {(['new', 'accepted'] as const).map(s => (
                <option key={s} value={s}>
                  {trackerStatusLabels[s]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tracked-item-next-action">Next action (optional)</Label>
          <Input
            id="tracked-item-next-action"
            maxLength={500}
            placeholder="e.g. JQ to provide a worked example"
            value={draft.nextAction}
            onChange={e => set('nextAction', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="tracked-item-waiting-on">Waiting on</Label>
            <select
              id="tracked-item-waiting-on"
              className={selectClass}
              value={draft.waitingOn}
              onChange={e => set('waitingOn', e.target.value as Draft['waitingOn'])}
            >
              <option value="">Nobody yet</option>
              {waitingOnOrder.map(w => (
                <option key={w} value={w}>
                  {waitingOnLabel(w, company?.name)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tracked-item-expected">Expected (optional)</Label>
            <Input id="tracked-item-expected" type="date" value={draft.expectedDate} onChange={e => set('expectedDate', e.target.value)} />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tracked-item-followers">Followers (optional)</Label>
          {followers.length > 0 && (
            <div className="space-y-1.5">
              {followers.map(f => (
                <Picked key={f._id} label={personName(f)} onClear={() => setFollowers(prev => prev.filter(p => p._id !== f._id))}>
                  <span className="text-etn-text-cell">{personName(f)}</span>
                </Picked>
              ))}
            </div>
          )}
          <Input
            id="tracked-item-followers"
            autoComplete="off"
            disabled={!company}
            placeholder={company ? 'Add people who should follow it' : 'Pick a company first'}
            value={followerQuery}
            onChange={e => setFollowerQuery(e.target.value)}
          />
          <SearchResults
            label="Matching followers"
            term={followerQuery}
            items={followerResults.data?.filter(u => !u.isAdmin && u._id !== requester?._id && !followers.some(f => f._id === u._id))}
            isFetching={followerResults.isFetching || followerQuery !== debouncedFollower}
            onPick={u => {
              setFollowers(prev => [...prev, u]);
              setFollowerQuery('');
            }}
            renderItem={u => <span className="text-etn-text-cell">{personName(u)}</span>}
          />
        </div>

        <label className="flex items-start gap-2 text-etn-control text-etn-text-cell">
          <input type="checkbox" className="mt-0.5" checked={draft.notify} onChange={e => set('notify', e.target.checked)} />
          <span>
            Notify the requester and followers
            <span className="block text-etn-micro text-etn-text-soft">Off: nobody is emailed, including the support team.</span>
          </span>
        </label>

        {error && (
          <p role="alert" className="text-sm text-etn-danger-text">
            {error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" disabled={!ready || create.isPending} onClick={submit}>
          {create.isPending ? 'Adding…' : 'Add to tracker'}
        </Button>
      </DialogFooter>
    </>
  );
}
